import { z } from "zod";

/**
 * Design tokens a brand supplies. Everything the core UI paints comes from
 * these CSS custom properties, so a brand can restyle every component without
 * touching markup or behaviour.
 */
const Color = z.string().min(1);

export const ColorTokens = z.object({
  bg: Color,
  surface: Color,
  surfaceRaised: Color,
  text: Color,
  textMuted: Color,
  accent: Color,
  accentContrast: Color,
  accentSoft: Color,
  border: Color,
  borderStrong: Color,
  danger: Color,
  success: Color,
  /** Colour of the 3D model when the scene preset does not override it. */
  model: Color,
  /** Viewport background behind the model. */
  stage: Color,
});
export type ColorTokens = z.infer<typeof ColorTokens>;

export const ThemeTokens = z.object({
  colors: ColorTokens,
  /** Optional overrides applied in dark mode (system preference or data-theme="dark"). */
  dark: ColorTokens.partial().optional(),
  fonts: z.object({
    display: z.string(),
    body: z.string(),
    mono: z.string(),
  }),
  /** Google Fonts family names to load, e.g. ["Architects Daughter", "JetBrains Mono:wght@400;600;800"]. */
  webFonts: z.array(z.string()).default([]),
  radius: z.object({ sm: z.string(), md: z.string(), lg: z.string(), pill: z.string().default("999px") }),
  spacing: z.object({ unit: z.number().positive().default(4) }),
  type: z.object({
    baseSize: z.string().default("14px"),
    ratio: z.number().default(1.2),
    labelTracking: z.string().default("0.06em"),
  }),
  shadows: z.object({ sm: z.string(), md: z.string(), lg: z.string() }),
  /** Free-form extras a brand's overrides.css may want, emitted as --cfg-x-<key>. */
  extras: z.record(z.string(), z.string()).default({}),
});
export type ThemeTokens = z.infer<typeof ThemeTokens>;
export type ThemeTokensInput = z.input<typeof ThemeTokens>;

export const PREFIX = "--cfg";

function kebab(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

function colorVars(colors: Partial<ColorTokens>): string[] {
  return Object.entries(colors).map(([k, v]) => `  ${PREFIX}-color-${kebab(k)}: ${v};`);
}

export interface CssOptions {
  /** Selector that carries the light palette. Default ":root". */
  selector?: string;
}

/**
 * Emit the tokens as CSS custom properties. Light values live on the selector;
 * dark overrides are emitted twice so both the system preference and an
 * explicit data-theme attribute win in either direction.
 */
export function tokensToCss(input: ThemeTokensInput, opts: CssOptions = {}): string {
  const t = ThemeTokens.parse(input);
  const sel = opts.selector ?? ":root";
  const lines: string[] = [];
  lines.push(`${sel} {`);
  lines.push(...colorVars(t.colors));
  lines.push(`  ${PREFIX}-font-display: ${t.fonts.display};`);
  lines.push(`  ${PREFIX}-font-body: ${t.fonts.body};`);
  lines.push(`  ${PREFIX}-font-mono: ${t.fonts.mono};`);
  lines.push(`  ${PREFIX}-radius-sm: ${t.radius.sm};`);
  lines.push(`  ${PREFIX}-radius-md: ${t.radius.md};`);
  lines.push(`  ${PREFIX}-radius-lg: ${t.radius.lg};`);
  lines.push(`  ${PREFIX}-radius-pill: ${t.radius.pill};`);
  lines.push(`  ${PREFIX}-space: ${t.spacing.unit}px;`);
  for (const n of [1, 2, 3, 4, 5, 6, 8, 10, 12]) lines.push(`  ${PREFIX}-space-${n}: ${t.spacing.unit * n}px;`);
  lines.push(`  ${PREFIX}-text-base: ${t.type.baseSize};`);
  const base = parseFloat(t.type.baseSize) || 14;
  const scale = { xs: -2, sm: -1, md: 0, lg: 1, xl: 2, "2xl": 3, "3xl": 5 } as const;
  for (const [name, step] of Object.entries(scale)) {
    lines.push(`  ${PREFIX}-text-${name}: ${(base * Math.pow(t.type.ratio, step)).toFixed(2)}px;`);
  }
  lines.push(`  ${PREFIX}-label-tracking: ${t.type.labelTracking};`);
  lines.push(`  ${PREFIX}-shadow-sm: ${t.shadows.sm};`);
  lines.push(`  ${PREFIX}-shadow-md: ${t.shadows.md};`);
  lines.push(`  ${PREFIX}-shadow-lg: ${t.shadows.lg};`);
  for (const [k, v] of Object.entries(t.extras)) lines.push(`  ${PREFIX}-x-${kebab(k)}: ${v};`);
  lines.push("}");

  if (t.dark && Object.keys(t.dark).length > 0) {
    const darkLines = colorVars(t.dark);
    lines.push(`@media (prefers-color-scheme: dark) {`);
    lines.push(`  ${sel}:not([data-theme="light"]) {`);
    lines.push(...darkLines.map((l) => `  ${l}`));
    lines.push("  }");
    lines.push("}");
    lines.push(`${sel}[data-theme="dark"] {`);
    lines.push(...darkLines);
    lines.push("}");
  }
  return lines.join("\n") + "\n";
}

/** Google Fonts stylesheet URL for the brand's web fonts, or undefined when none. */
export function webFontsUrl(t: Pick<ThemeTokens, "webFonts">): string | undefined {
  if (!t.webFonts.length) return undefined;
  const families = t.webFonts.map((f) => `family=${encodeURIComponent(f).replace(/%3A/g, ":").replace(/%40/g, "@").replace(/%3B/g, ";").replace(/%20/g, "+")}`);
  return `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
}

/**
 * Tailwind preset that maps utility names onto the CSS variables, so brand
 * code can write `bg-surface text-accent rounded-md` and stay themeable.
 */
export function createTailwindPreset(input: ThemeTokensInput) {
  const t = ThemeTokens.parse(input);
  const colors = Object.fromEntries(Object.keys(t.colors).map((k) => [kebab(k), `var(${PREFIX}-color-${kebab(k)})`]));
  return {
    theme: {
      extend: {
        colors,
        fontFamily: {
          display: `var(${PREFIX}-font-display)`,
          body: `var(${PREFIX}-font-body)`,
          mono: `var(${PREFIX}-font-mono)`,
        },
        borderRadius: {
          sm: `var(${PREFIX}-radius-sm)`,
          md: `var(${PREFIX}-radius-md)`,
          lg: `var(${PREFIX}-radius-lg)`,
          pill: `var(${PREFIX}-radius-pill)`,
        },
        boxShadow: {
          sm: `var(${PREFIX}-shadow-sm)`,
          md: `var(${PREFIX}-shadow-md)`,
          lg: `var(${PREFIX}-shadow-lg)`,
        },
      },
    },
  };
}

/** Inject (or replace) a style tag with the brand tokens. Browser only. */
export function applyTheme(input: ThemeTokensInput, id = "cfg-theme"): void {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    document.head.prepend(el);
  }
  el.textContent = tokensToCss(input);
  const url = webFontsUrl(ThemeTokens.parse(input));
  const linkId = `${id}-fonts`;
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  if (url) {
    if (!link) {
      link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
  } else if (link) {
    link.remove();
  }
}

/** A neutral fallback palette used when a brand omits tokens (and by tests). */
export const neutralTokens: ThemeTokensInput = {
  colors: {
    bg: "#f6f7f9",
    surface: "#ffffff",
    surfaceRaised: "#ffffff",
    text: "#1b1f27",
    textMuted: "#5d6573",
    accent: "#2f6fed",
    accentContrast: "#ffffff",
    accentSoft: "rgba(47,111,237,0.12)",
    border: "#d9dee7",
    borderStrong: "#aab3c2",
    danger: "#c0392b",
    success: "#1f8a4c",
    model: "#d84a3a",
    stage: "#eef1f5",
  },
  dark: {
    bg: "#0f1218",
    surface: "#171b23",
    surfaceRaised: "#1e242e",
    text: "#e8ecf3",
    textMuted: "#9aa4b5",
    border: "#2a313d",
    borderStrong: "#3c4655",
    stage: "#12161d",
  },
  fonts: {
    display: "Inter, system-ui, sans-serif",
    body: "Inter, system-ui, sans-serif",
    mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  radius: { sm: "4px", md: "8px", lg: "12px" },
  spacing: { unit: 4 },
  type: { baseSize: "14px", ratio: 1.2 },
  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.08)",
    md: "0 4px 12px rgba(0,0,0,0.10)",
    lg: "0 12px 32px rgba(0,0,0,0.14)",
  },
};
